/**
 * Central icon registry — maps semantic names to icon components.
 * Import from here instead of react-icons directly so that changing
 * an icon only requires editing this file.
 *
 * Usage:
 *   import { AppIcons } from "constants/icons";
 *   <Icon as={AppIcons.workflow} />
 */
import { LuWorkflow, LuLineChart, LuBarChart3, LuPlay, LuTerminal, LuInfo, LuSparkles } from "react-icons/lu";
import {
  MdSchedule,
  MdCode,
  MdOutlineFileDownload,
  MdOutlineSegment,
  MdBarChart,
  MdNumbers,
  MdMemory,
  MdEdit,
  MdErrorOutline,
  MdCheckCircle,
  MdInput,
  MdOutlineIntegrationInstructions,
  MdSupportAgent,
  MdNotes,
  MdOutlineDataObject,
  MdAddCircleOutline,
  MdUploadFile,
  MdOutlineUploadFile,
  MdInsertDriveFile,
  MdImage,
  MdAttachFile,
} from "react-icons/md";
import { BsThreeDotsVertical, BsFiletypePng, BsFiletypeJpg, BsRobot } from "react-icons/bs";
import {
  FaChartLine,
  FaPuzzlePiece,
  FaArrowLeft,
  FaArrowUp,
  FaCheck,
  FaUndo,
  FaLongArrowAltDown,
  FaMinus,
  FaPlus,
  FaRegFileExcel,
  FaFilter,
  FaChevronUp,
  FaChevronDown,
  FaChartPie,
  FaFileAlt,
  FaFileWord,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
  FaRegLightbulb,
  FaChevronRight,
  FaHistory,
  FaPause,
  FaTimes,
  FaTrash,
  FaTools,
  FaFlask,
  FaUsers,
  FaBookOpen,
  FaLock,
  FaUnlock,
  FaMagic,
  FaCheckCircle,
  FaRegCheckCircle,
  FaCubes,
  FaTasks,
  FaReplyAll,
  FaCube,
} from "react-icons/fa";
import { FaTableList, FaList } from "react-icons/fa6";
import {
  TbMathFunction,
  TbLayoutSidebarRightCollapseFilled,
  TbBrandPython,
  TbLayoutSidebarLeftExpandFilled,
  TbLayoutSidebarRightExpandFilled,
  TbLogout2,
  TbPlugConnected,
} from "react-icons/tb";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { HiOutlineDatabase } from "react-icons/hi";
import { TiFlowMerge } from "react-icons/ti";
import {
  AddIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  HamburgerIcon,
  InfoOutlineIcon,
  ExternalLinkIcon,
  RepeatIcon,
  ViewIcon,
  ViewOffIcon,
} from "@chakra-ui/icons";
import {
  IoChatbubble,
  IoChatbubbleOutline,
  IoChatbubblesOutline,
  IoCopyOutline,
  IoSparklesSharp,
  IoDuplicate,
} from "react-icons/io5";
import {
  FiCalendar,
  FiCodesandbox,
  FiPlus,
  FiSave,
  FiEdit,
  FiEdit2,
  FiSearch,
  FiSettings,
  FiShare2,
  FiTrash,
  FiTrash2,
  FiX,
  FiCopy,
  FiDownload,
  FiMap,
  FiLayers,
  FiClock,
  FiUser,
} from "react-icons/fi";
import { PiTarget, PiBracketsCurlyBold } from "react-icons/pi";
import { BiSolidNetworkChart } from "react-icons/bi";
import { GoHubot } from "react-icons/go";
import { GiBrain } from "react-icons/gi";
import { RiRobotFill } from "react-icons/ri";
import { VscSymbolVariable, VscSymbolProperty } from "react-icons/vsc";
import { IoIosOptions } from "react-icons/io";

export const AppIcons = {
  analyst: BsRobot,
  workflow: LuWorkflow,
  workflowRun: MdSchedule,
  analystScope: BiSolidNetworkChart,
  analystMemory: MdMemory,
  menuDots: BsThreeDotsVertical,
  analyseData: LuLineChart,
  buildChart: LuBarChart3,
  runWorkflow: LuPlay,
  leftArrow: FaArrowLeft,
  sendArrow: FaArrowUp,
  check: FaCheck,
  undo: FaUndo,
  search: FiSearch,
  share: FiShare2,
  chart: FaChartLine,
  dataframe: FaTableList,
  loadSkill: FaPuzzlePiece,
  mathFunction: TbMathFunction,
  collapseSidebar: TbLayoutSidebarRightCollapseFilled,
  python: TbBrandPython,
  database: HiOutlineDatabase,
  list: FaList,
  codeAlt: MdCode,
  arrowDown: FaLongArrowAltDown,
  minus: FaMinus,
  plus: FaPlus,
  fileExcel: FaRegFileExcel,
  filePng: BsFiletypePng,
  fileJpg: BsFiletypeJpg,
  flowMerge: TiFlowMerge,
  viewOn: ViewIcon,
  viewOff: ViewOffIcon,
  download: MdOutlineFileDownload,
  copy: IoCopyOutline,
  blocks: MdOutlineSegment,
  filter: FaFilter,
  chevronUp: FaChevronUp,
  chevronDown: FaChevronDown,
  dataModel: FaCube,
  knowledge: GiBrain,
  metric: MdBarChart,
  attribute: FaChartPie,
  timeframe: FiCalendar,
  benchmark: PiTarget,
  measure: MdNumbers,
  edit: MdEdit,
  analystChat: IoChatbubble,
  document: FaFileWord,
  eye: FaEye,
  eyeOff: FaEyeSlash,
  warning: FaExclamationTriangle,
  chevronRight: FaChevronRight,
  lightbulb: FaRegLightbulb,
  checkCircle: MdCheckCircle,
  errorOutline: MdErrorOutline,
  refresh: RepeatIcon,
  terminal: LuTerminal,
  leftIcon: ChevronLeftIcon,
  rightIcon: ChevronRightIcon,
  downIcon: ChevronDownIcon,
  info: LuInfo,
  file: FaFileAlt,
  close: FaTimes,
  delete: FaTrash,
  history: FaHistory,
  pause: FaPause,
  inputs: MdInput,
  chatSessions: IoChatbubblesOutline,
  chatBubble: IoChatbubbleOutline,
  results: IoSparklesSharp,
  sparkles: LuSparkles,
  guidance: MdOutlineIntegrationInstructions,
  findings: HiOutlineDocumentReport,
  // EVA sidebar
  evaTitle: MdSupportAgent,
  evaAgents: GoHubot,
  evaTools: FaTools,
  evaSkills: FaPuzzlePiece,
  evaPlayground: FiCodesandbox,
  evaExperiments: FaFlask,
  users: FaUsers,
  evaUsageMetrics: MdBarChart,
  // Actions
  add: AddIcon,
  // Feather outline variants — thin/outline style for table action buttons
  plusLine: FiPlus,
  editLine: FiEdit,
  deleteLine: FiTrash2,
  trashLine: FiTrash,
  closeLine: FiX,
  copyLine: FiCopy,
  downloadLine: FiDownload,
  // Eva-specific icons
  evaAgent: MdSupportAgent,
  agentBot: GoHubot,
  playground: FiCodesandbox,
  tools: FaTools,
  flask: FaFlask,
  docs: FaBookOpen,
  locked: FaLock,
  unlocked: FaUnlock,
  generate: FaMagic,
  checkFilled: FaCheckCircle,
  checkOutline: FaRegCheckCircle,
  duplicate: IoDuplicate,
  changelog: MdNotes,
  infoOutline: InfoOutlineIcon,
  save: FiSave,
  cubes: FaCubes,
  tasks: FaTasks,
  responseOutput: FaReplyAll,
  robot: RiRobotFill,
  externalLink: ExternalLinkIcon,
  curlyBrackets: PiBracketsCurlyBold,
  dataObject: MdOutlineDataObject,
  variable: VscSymbolVariable,
  property: VscSymbolProperty,
  trash: FiTrash,
  cancelX: FiX,
  // Sidebar toggle
  sidebarOpen: TbLayoutSidebarLeftExpandFilled,
  sidebarClose: TbLayoutSidebarRightExpandFilled,
  // User / navigation
  logout: TbLogout2,
  settings: FiSettings,
  editAlt: FiEdit2,
  trashAlt: FiTrash2,
  hamburger: HamburgerIcon,
  newChat: MdAddCircleOutline,
  // Report template
  upload: MdUploadFile,
  uploadOutline: MdOutlineUploadFile,
  fileGeneric: MdInsertDriveFile,
  image: MdImage,
  attachment: MdAttachFile,
  minimap: FiMap,
  layers: FiLayers,
  connection: TbPlugConnected,
  clock: FiClock,
  user: FiUser,
  modelActions: IoIosOptions,
} as const;

export type AppIconKey = keyof typeof AppIcons;
